import {
  ButtonList,
  DropdownButton,
  DropdownButtonContext,
} from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';

import { UserProfile } from 'components/userProfile/userProfile';
import { useCurrentUser } from 'utils/user';

import { useConversationRow } from '../../../../../utils/useConversationRow';

import { ConversationsTableRowMenuActivityLog } from './components/items/activityLog/conversationsTableRowMenuActivityLog';
import { ConversationsTableRowMenuClose } from './components/items/close/conversationsTableRowMenuClose';
import { ConversationsTableRowMenuFilter } from './components/items/filter/conversationsTableRowMenuFilter';
import { ConversationsTableRowMenuTransferToQueue } from './components/items/transferToQueue/conversationsTableRowMenuTransferToQueue';
import { ConversationsTableRowMenuUser } from './components/items/user/conversationsTableRowMenuUser';
import styles from './conversationsTableRowMenu.module.scss';

/**
 * Represents the component for displaying various actions that can be performed
 * on each conversation in the table.
 */
export const ConversationsTableRowMenu = (): JSX.Element => {
  const { conversation, showRowMenuFilter = true } = useConversationRow();
  const { user } = useCurrentUser();

  const hasReplyAccess = useHasAccess('conversation.reply');
  const hasLogAccess = useHasAccess('conversation.log');

  const isUnassignedOrAssignedToMe =
    !conversation.assigned_to || user.id === conversation.assigned_to.id;
  const canTransferConversation =
    conversation.initial_channel !== 'callback' && isUnassignedOrAssignedToMe;
  const canCloseOrEndConversation =
    !conversation.is_closed && isUnassignedOrAssignedToMe;

  return (
    <DropdownButton
      noArrow
      fixed
      icon="dots-v"
      buttonProps={{ variant: 'tertiary' }}
      dropdownProps={{ className: styles.dropdown, fixed: true }}
      dropdown={
        <DropdownButtonContext.Consumer>
          {({ toggleDropdown }) => (
            <ButtonList>
              {hasLogAccess && (
                <ConversationsTableRowMenuActivityLog
                  conversation={conversation}
                />
              )}

              {hasReplyAccess && canTransferConversation && (
                <ConversationsTableRowMenuTransferToQueue
                  conversation={conversation}
                  toggleDropdown={toggleDropdown}
                />
              )}

              {hasReplyAccess && canCloseOrEndConversation && (
                <ConversationsTableRowMenuClose
                  conversation={conversation}
                  toggleDropdown={toggleDropdown}
                />
              )}

              {conversation.requested_by && (
                <>
                  <ButtonList.Divider />

                  <div className={styles.header}>
                    <UserProfile
                      user={conversation.requested_by}
                      truncateName
                      nameClassName={styles.userName}
                    />
                  </div>

                  <ConversationsTableRowMenuUser
                    user={conversation.requested_by}
                  />

                  {showRowMenuFilter && (
                    <ConversationsTableRowMenuFilter
                      user={conversation.requested_by}
                      toggleDropdown={toggleDropdown}
                    />
                  )}
                </>
              )}

              {conversation.assigned_to && (
                <>
                  <ButtonList.Divider />

                  <div className={styles.header}>
                    <UserProfile
                      user={conversation.assigned_to}
                      truncateName
                      nameClassName={styles.userName}
                    />
                  </div>

                  <ConversationsTableRowMenuUser
                    user={conversation.assigned_to}
                    isAssignee
                  />

                  {showRowMenuFilter && (
                    <ConversationsTableRowMenuFilter
                      user={conversation.assigned_to}
                      isAssignee
                      toggleDropdown={toggleDropdown}
                    />
                  )}
                </>
              )}
            </ButtonList>
          )}
        </DropdownButtonContext.Consumer>
      }
    />
  );
};
